'use client';

import { c as _c } from "react/compiler-runtime";
import type { TFunction } from 'i18next';
import { createSerializer } from 'nuqs';
import { useState } from 'react';
import { throttle } from 'throttle-debounce';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { countryIsoToRegionIso, programCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import type { ChildSearchEvent } from '@sola/tracking/components/datalayer.tsx';
import { generateDayItems, generateMonthItems } from '@sola/utils/date.ts';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { SacFiltersEnum, sacQueryParamsSchema } from '../../utils/query-params.sac.tsx';
import { resolveChipFilters } from '../sac-list/sac-list.logic.ts';
const {
  sacMinAge,
  sacMaxAge
} = generalConfig;
export const buildGenderOptions = (t: TFunction<['children', 'core', 'geo']>) => [{
  value: ChildGenderEnum.Male,
  text: t('children:sac.controls.boy')
}, {
  value: ChildGenderEnum.Female,
  text: t('children:sac.controls.girl')
}, {
  value: ChildGenderEnum.Either,
  text: t('children:sac.controls.either')
}];
export const buildCountryOptions = (t: TFunction<['children', 'core', 'geo']>, isMultipleCountry: boolean | null | undefined) => [{
  value: '',
  text: t('children:sac.controls.allCountries')
}, ...(isMultipleCountry ? [{
  value: 'multiple',
  text: t('children:sac.controls.multipleCountries'),
  disabled: true
}] : []), ...programCountryIsoCodes.map(c => ({
  value: c,
  text: t(`geo:countries.${c}.nameS` as 'geo:countries.BD.nameS')
})).toSorted((a, b) => a.text.localeCompare(b.text))];
export const buildMonthOptions = (t: TFunction<['children', 'core', 'geo']>) => [{
  value: '0',
  text: t('children:sac.controls.month')
}, ...generateMonthItems()];
export const buildDayOptions = (t: TFunction<['children', 'core', 'geo']>) => [{
  value: '0',
  text: t('children:sac.controls.day')
}, ...generateDayItems()];
export const buildChipItems = (t: TFunction<['children', 'core', 'geo']>) => [{
  name: t('children:sac.orphanedChild'),
  value: SacFiltersEnum.enum.orphanedChild,
  icon: 'child' as IconNames
}, {
  name: t('children:sac.hasDisabilities'),
  value: SacFiltersEnum.enum.hasDisabilities,
  icon: 'wheelchair' as IconNames
}, {
  name: t('children:sac.vulnerableToExploitation'),
  value: SacFiltersEnum.enum.vulnerableExploitation,
  icon: 'alert-triangle' as IconNames
}, {
  name: t('children:sac.affectedByHiv'),
  value: SacFiltersEnum.enum.affectedHiv,
  icon: 'virus' as IconNames
}];
export const buildTrackingEvent = (sacFilters?: SacQueryParams): ChildSearchEvent => {
  const chips = resolveChipFilters(sacFilters?.filters);
  return {
    gender: sacFilters?.gender || undefined,
    ageMin: sacFilters?.ageMin || undefined,
    ageMax: sacFilters?.ageMax || undefined,
    region: countryIsoToRegionIso(sacFilters?.countries?.[0] || ''),
    country: [...(sacFilters?.countries || [])],
    birthMonth: sacFilters?.birthMonth || undefined,
    birthDay: sacFilters?.birthDay || undefined,
    orphan: chips.isOrphan,
    disability: chips.disability,
    highlyVulnerable: chips.isVulnerable,
    hiv: chips.isInHivAffectedArea
  };
};
const serializeSacFilters = createSerializer(sacQueryParamsSchema);
export const getSerializedSacUrl = (sacFilters?: SacQueryParams) => `/sponsor-a-child${serializeSacFilters(sacFilters ?? {})}`;
export const useSacControlsState = (sacFilters, setSacFilters) => {
  const $ = _c(28);
  const [showAdvanced, setShowAdvanced] = useState(sacFilters?.gender !== ChildGenderEnum.Either || sacFilters?.ageMin !== undefined || sacFilters?.ageMax !== undefined || (sacFilters?.countries?.length ?? 0) > 0);
  const t0 = sacFilters?.ageMin ?? sacMinAge;
  const t1 = sacFilters?.ageMax ?? sacMaxAge;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = [t0, t1];
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const ageRange = t2;
  const [isMultipleCountry, setIsMultipleCountry] = useState(sacFilters?.countries && sacFilters.countries.length > 1);
  let t3;
  if ($[3] !== setSacFilters) {
    t3 = throttle(300, (min, max) => {
      setSacFilters?.({
        ageMin: min,
        ageMax: max
      });
    }, {
      noLeading: true
    });
    $[3] = setSacFilters;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const throttledSliderCommit = t3;
  let t4;
  if ($[5] !== setSacFilters) {
    t4 = v => {
      setSacFilters?.({
        gender: v?.target?.value as ChildGenderEnum
      });
    };
    $[5] = setSacFilters;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleGenderChange = t4;
  let t5;
  if ($[7] !== throttledSliderCommit) {
    t5 = t6 => {
      const {
        newValue
      } = t6;
      if (newValue?.length === 2) {
        const [min_0, max_0] = newValue;
        throttledSliderCommit(min_0, max_0);
      }
    };
    $[7] = throttledSliderCommit;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleAgeSliderCommit = t5;
  let t6;
  if ($[9] !== setSacFilters) {
    t6 = v_0 => {
      setIsMultipleCountry(false);
      setSacFilters?.({
        countries: [v_0?.target?.value].filter(Boolean)
      });
    };
    $[9] = setSacFilters;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleCountryChange = t6;
  let t7;
  if ($[11] !== setSacFilters) {
    t7 = v_1 => {
      setSacFilters?.({
        birthMonth: Number(v_1?.target?.value)
      });
    };
    $[11] = setSacFilters;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleBirthMonthChange = t7;
  let t8;
  if ($[13] !== setSacFilters) {
    t8 = v_2 => {
      setSacFilters?.({
        birthDay: Number(v_2?.target?.value)
      });
    };
    $[13] = setSacFilters;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleBirthDayChange = t8;
  let t9;
  if ($[15] !== setSacFilters) {
    t9 = v_3 => {
      setSacFilters?.({
        filters: v_3 as SacFiltersEnum[]
      });
    };
    $[15] = setSacFilters;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleChipChange = t9;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setShowAdvanced(_temp);
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const toggleAdvanced = t10;
  let t11;
  if ($[18] !== ageRange || $[19] !== handleAgeSliderCommit || $[20] !== handleBirthDayChange || $[21] !== handleBirthMonthChange || $[22] !== handleChipChange || $[23] !== handleCountryChange || $[24] !== handleGenderChange || $[25] !== isMultipleCountry || $[26] !== showAdvanced) {
    t11 = {
      showAdvanced,
      ageRange,
      isMultipleCountry,
      sacMinAge,
      sacMaxAge,
      handleGenderChange,
      handleAgeSliderCommit,
      handleCountryChange,
      handleBirthMonthChange,
      handleBirthDayChange,
      handleChipChange,
      toggleAdvanced
    };
    $[18] = ageRange;
    $[19] = handleAgeSliderCommit;
    $[20] = handleBirthDayChange;
    $[21] = handleBirthMonthChange;
    $[22] = handleChipChange;
    $[23] = handleCountryChange;
    $[24] = handleGenderChange;
    $[25] = isMultipleCountry;
    $[26] = showAdvanced;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
function _temp(v_4) {
  return !v_4;
}